/**
 * Iframe-side Permissions Policy detection.
 *
 * Modern browsers expose `document.permissionsPolicy.allowsFeature(name)`
 * (older Chrome shipped this as `featurePolicy`). When the parent page
 * sends `Permissions-Policy: camera=(), microphone=()`, the iframe's
 * permissions policy will deny these features. getUserMedia would then
 * silently NotAllowedError without prompting. This helper detects that
 * upfront so the embed can pivot to a "continue in new tab" fallback
 * instead of leaving the user staring at an empty mic-level meter.
 *
 * @module lib/embed/permission-check
 */

interface PolicyApi {
  allowsFeature(name: string): boolean;
}

/**
 * Returns true when camera or microphone is blocked by the document's
 * Permissions Policy. False when allowed, when the API is unavailable,
 * or when the check throws. Voice-only calls do not require camera access.
 */
export function isMediaBlockedByPolicy(requiresCamera = true): boolean {
  if (typeof document === 'undefined') return false;
  const doc = document as unknown as {
    permissionsPolicy?: PolicyApi;
    featurePolicy?: PolicyApi;
  };
  const api = doc.permissionsPolicy ?? doc.featurePolicy;
  if (!api || typeof api.allowsFeature !== 'function') return false;
  try {
    return !api.allowsFeature('microphone') || (requiresCamera && !api.allowsFeature('camera'));
  } catch {
    return false;
  }
}
